import { supabase } from '@/lib/supabaseClient';

const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/, '');
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

function supabaseRestHeaders() {
  return {
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${supabaseAnonKey}`,
    'Content-Type': 'application/json',
    Prefer: 'return=minimal',
  };
}

/** Build insert body matching leads table columns. */
function toLeadRow(payload) {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: payload.name,
    phone: payload.phone,
    from_city: payload.from_city,
    to_city: payload.to_city,
    travel_date: payload.travel_date,
    vehicle: payload.vehicle,
    source: payload.source ?? 'WhatsApp Form',
    email: payload.email ?? null,
    pickup_time: payload.pickup_time ?? null,
    passengers: payload.passengers ?? 1,
    trip_type: payload.trip_type ?? null,
    notes: payload.notes ?? null,
    status: payload.status ?? 'New',
    created_at: now,
    updated_at: now,
  };
}

/**
 * POST with keepalive + full Supabase headers — survives WhatsApp redirect.
 * sendBeacon cannot set Authorization, so it is not used for Supabase REST.
 */
function postLeadKeepalive(row) {
  if (!supabaseUrl || !supabaseAnonKey) return;
  fetch(`${supabaseUrl}/rest/v1/leads`, {
    method: 'POST',
    headers: supabaseRestHeaders(),
    body: JSON.stringify(row),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Fire-and-forget lead save — does not block WhatsApp redirect.
 * Uses keepalive fetch (all devices) plus supabase-js insert as backup.
 */
export function saveLeadInBackground(payload) {
  const row = toLeadRow(payload);

  // TEMP: inspect exact POST body before Supabase request
  // eslint-disable-next-line no-console
  console.log('[saveLead] POST body:', JSON.stringify(row));
  // eslint-disable-next-line no-console
  console.log(
    '[saveLead] field types:',
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        key,
        value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value,
      ]),
    ),
  );

  postLeadKeepalive(row);

  // Backup path — supabase client sets auth headers correctly
  supabase.from('leads').insert(row).then(({ error }) => {
    if (error && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Lead insert via supabase-js failed:', error.message);
    }
  });
}

/** Awaitable save for flows that need confirmation before continuing. */
export async function saveLead(payload) {
  const row = toLeadRow(payload);

  // TEMP: inspect exact POST body before Supabase request
  // eslint-disable-next-line no-console
  console.log('[saveLead] POST body:', JSON.stringify(row));
  // eslint-disable-next-line no-console
  console.log(
    '[saveLead] field types:',
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        key,
        value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value,
      ]),
    ),
  );

  const { error } = await supabase.from('leads').insert(row);
  if (!error) return true;

  try {
    const res = await fetch(`${supabaseUrl}/rest/v1/leads`, {
      method: 'POST',
      headers: supabaseRestHeaders(),
      body: JSON.stringify(row),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

// re-export for tests / debugging
export { toLeadRow };
